import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/shared/Sidebar';
import MarketTable from '../components/market/MarketTable';
import MarketForm from '../components/market/MarketForm';
import MarketService from '../services/marketService';
import { Market } from '../types/Market';

const MarketMaintenancePage: React.FC = () => {
  const navigate = useNavigate();
  const [markets, setMarkets] = useState<Market[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [editingMarket, setEditingMarket] = useState<Market | null>(null);
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);

  const fetchMarkets = async () => {
    setLoading(true);
    try {
      const data = await MarketService.getAllMarkets();
      setMarkets(data);
      setError(null);
    } catch (err) {
      setError('Error loading markets. Please try again later.');
      console.error('Error fetching markets:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMarkets();
  }, []);

  const handleCreateMarket = () => {
    setEditingMarket(null);
    setIsFormOpen(true);
  };

  const handleEditMarket = (market: Market) => {
    setEditingMarket(market);
    setIsFormOpen(true);
  };

  const handleDeleteMarket = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this market?')) {
      return;
    }

    try {
      await MarketService.deleteMarket(id);
      // Refresh the market list
      fetchMarkets();
    } catch (err: any) {
      setError('Error deleting market. Please try again.');
      console.error('Error deleting market:', err);
    }
  };

  const handleFormSubmit = async (market: Market) => {
    try {
      if (editingMarket) {
        // Update existing market
        await MarketService.updateMarket(editingMarket.id!, market);
      } else {
        // Create new market
        await MarketService.createMarket(market);
      }

      // Close the form and refresh markets
      setIsFormOpen(false);
      setEditingMarket(null);
      fetchMarkets();
    } catch (err: any) {
      setError(err.message || 'Error saving market. Please try again.');
      console.error('Error saving market:', err);
    }
  };

  const handleFormCancel = () => {
    setIsFormOpen(false);
    setEditingMarket(null);
  };

  const goToDashboard = () => {
    navigate('/dashboard');
  };

  return (
    <div className="dashboard-page">
      <Sidebar />
      <div className="dashboard-main">
        <div className="dashboard-content">
          <div className="breadcrumbs">
            <button className="breadcrumb-link" onClick={goToDashboard}>
              Dashboard
            </button>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">Market Maintenance</span>
          </div>

          <div className="content-container">
            <div className="page-header">
              <h1>Market Maintenance</h1>
              <button 
                className="primary-button"
                onClick={handleCreateMarket}
                disabled={isFormOpen}
              >
                Add New Market
              </button>
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            {isFormOpen ? (
              <MarketForm 
                market={editingMarket}
                onSubmit={handleFormSubmit}
                onCancel={handleFormCancel}
              />
            ) : (
              <MarketTable
                markets={markets}
                loading={loading}
                onEdit={handleEditMarket}
                onDelete={handleDeleteMarket}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketMaintenancePage; 